import React, { useEffect, useMemo, useState } from "react";
import { PageHeader, DataTable, StatusBadge } from "@/components/common";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Clock } from "lucide-react";
import { useAuth } from "@/lib/auth";

const emptyToday = () => new Date().toISOString().slice(0, 10);
const emptyPlus30 = () => new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
const empty = () => ({
  municipality_id: "", name: "", target_url: "",
  start_date: emptyToday(), end_date: emptyPlus30(),
  start_time: "00:00", end_time: "23:59",
  priority: 5, status: "active", notes: "",
});

export default function CampaignsPage() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [muns, setMuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [filterMun, setFilterMun] = useState("all");

  async function load() {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([api.get("/admin/campaigns"), api.get("/admin/municipalities")]);
      setRows(c.data); setMuns(m.data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filteredRows = useMemo(() => (
    filterMun === "all" ? rows : rows.filter((r) => r.municipality_id === filterMun)
  ), [rows, filterMun]);

  function openNew() {
    setEditing(null);
    setForm({ ...empty(), municipality_id: muns[0]?.id || "" });
    setOpen(true);
  }
  function openEdit(r) {
    setEditing(r);
    setForm({
      municipality_id: r.municipality_id, name: r.name, target_url: r.target_url,
      start_date: r.start_date, end_date: r.end_date,
      start_time: r.start_time || "00:00", end_time: r.end_time || "23:59",
      priority: r.priority || 5, status: r.status || "active", notes: r.notes || "",
    });
    setOpen(true);
  }
  async function save() {
    try {
      const body = { ...form, priority: parseInt(form.priority, 10) || 5 };
      if (editing) { await api.put(`/admin/campaigns/${editing.id}`, body); toast.success("Kampanya güncellendi"); }
      else { await api.post("/admin/campaigns", body); toast.success("Kampanya eklendi"); }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }
  async function remove(r) {
    const msg = [
      `${r.name} kampanyası kalıcı olarak silinsin mi?`,
      "",
      "• Kampanyaya bağlı TÜM görseller (webp + jpg dosyaları) storage'dan silinir.",
      "• Bu işlem geri alınamaz.",
      "• Ancak geçmiş istatistikler raporlarda kampanya adıyla birlikte görünmeye devam eder.",
    ].join("\n");
    if (!window.confirm(msg)) return;
    try {
      const res = await api.delete(`/admin/campaigns/${r.id}`);
      toast.success(`Silindi · ${res.data.deleted_creatives || 0} kreatif + ${res.data.storage_files_removed || 0} dosya temizlendi`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        overline="Reklamverenler"
        title="Kampanyalar"
        description="Belediyelere ait aktif ve zamanlanmış reklam kampanyaları. Kampanyalar günlük tarih aralığına ek olarak, günün belirli saatlerinde de yayınlanacak şekilde zamanlanabilir (TSİ)."
        actions={isSuper && (
          <Button className="bg-red-600 hover:bg-red-700 rounded-sm h-10 font-heading font-semibold uppercase text-xs tracking-wider" onClick={openNew} data-testid="add-campaign-button">
            <Plus className="w-4 h-4 mr-1.5" /> Yeni Kampanya
          </Button>
        )}
      />

      {isSuper && (
        <div className="flex items-center gap-3">
          <Label className="text-xs text-slate-600">Belediye Filtresi</Label>
          <Select value={filterMun} onValueChange={setFilterMun}>
            <SelectTrigger className="w-64 h-9" data-testid="camp-mun-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü ({rows.length})</SelectItem>
              {muns.map((m) => {
                const cnt = rows.filter((r) => r.municipality_id === m.id).length;
                return <SelectItem key={m.id} value={m.id}>{m.name} ({cnt})</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
      )}

      {loading ? <div className="text-sm text-slate-500">Yükleniyor…</div> : (
        <DataTable
          testid="campaigns-table"
          columns={[
            { label: "Kampanya", render: (r) => <div><div className="font-medium">{r.name}</div><div className="text-xs text-slate-500 truncate max-w-md font-mono">{r.target_url}</div></div> },
            { label: "Belediye", render: (r) => r.municipality_name },
            { label: "Tarih", render: (r) => <span className="text-xs font-mono">{r.start_date} → {r.end_date}</span> },
            { label: "Saat", render: (r) => (
                <span className="text-xs font-mono inline-flex items-center gap-1 text-slate-600">
                  <Clock className="w-3 h-3" /> {r.start_time || "00:00"}–{r.end_time || "23:59"}
                </span>
              ) },
            { label: "Öncelik", render: (r) => <span className="font-heading font-semibold">{r.priority}</span> },
            { label: "Kreatif", render: (r) => r.creative_count || 0 },
            { label: "Durum", render: (r) => <StatusBadge status={r.status} /> },
            {
              label: "İşlemler", render: (r) => isSuper && (
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-camp-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => remove(r)} data-testid={`delete-camp-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              )
            },
          ]}
          rows={filteredRows}
          empty="Bu belediye için kampanya yok."
        />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Kampanya Düzenle" : "Yeni Kampanya"}</DialogTitle>
            <DialogDescription>Belediye reklam kampanyasının hedef URL, tarih aralığı ve günlük TSİ saat aralığını tanımlayın.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Belediye</Label>
              <Select value={form.municipality_id} onValueChange={(v) => setForm({ ...form, municipality_id: v })}>
                <SelectTrigger data-testid="campaign-form-mun"><SelectValue placeholder="Belediye" /></SelectTrigger>
                <SelectContent>{muns.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Kampanya Adı</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="campaign-form-name" /></div>
            <div><Label>Hedef URL</Label><Input value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} placeholder="https://belediye.tr/kampanya" /></div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Başlangıç Tarihi</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="campaign-form-start" /></div>
              <div><Label>Bitiş Tarihi</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="campaign-form-end" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="flex items-center gap-1"><Clock className="w-3 h-3" /> Günlük Başlangıç (TSİ)</Label>
                <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="campaign-form-start-time" />
              </div>
              <div>
                <Label className="flex items-center gap-1"><Clock className="w-3 h-3" /> Günlük Bitiş (TSİ)</Label>
                <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="campaign-form-end-time" />
              </div>
              <div className="col-span-2 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
                💡 Kampanya, tarih aralığındaki her günün <b>{form.start_time}</b> — <b>{form.end_time}</b> saatleri arasında yayında olur. Saatler <b>TSİ</b> (Türkiye Standart Saati) baz alınır. Tüm gün için <span className="font-mono">00:00 – 23:59</span> bırakın.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Öncelik (1-10)</Label><Input type="number" min="1" max="10" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} /></div>
              <div>
                <Label>Durum</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Aktif</SelectItem><SelectItem value="inactive">Pasif</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Notlar</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} className="bg-red-600 hover:bg-red-700 rounded-sm" data-testid="campaign-form-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
